import React, { useEffect, useRef, useState } from 'react';
import { brandingAPI, uploadAPI, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Save,
  Loader2,
  Palette,
  Building,
  User,
  Image,
  Upload,
  X,
  Mail,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

export default function Branding() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    organization_name: '',
    logo_url: '',
    primary_color: '#0F172A',
    accent_color: '#0EA5E9',
    signatory_name: '',
    signatory_title: '',
    president_name: '',
    president_title: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    abn: '',
    bank_account_name: '',
    bank_bsb: '',
    bank_account_number: ''
  });

  useEffect(() => {
    fetchBranding();
    fetchEmailStatus();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const res = await brandingAPI.getEmailStatus();
      setEmailStatus(res.data);
    } catch (e) {
      // non-blocking
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await brandingAPI.get();
      setFormData({
        organization_name: response.data.organization_name || '',
        logo_url: response.data.logo_url || '',
        primary_color: response.data.primary_color || '#0F172A',
        accent_color: response.data.accent_color || '#0EA5E9',
        signatory_name: response.data.signatory_name || '',
        signatory_title: response.data.signatory_title || '',
        president_name: response.data.president_name || '',
        president_title: response.data.president_title || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        address: response.data.address || '',
        abn: response.data.abn || '',
        bank_account_name: response.data.bank_account_name || '',
        bank_bsb: response.data.bank_bsb || '',
        bank_account_number: response.data.bank_account_number || ''
      });
    } catch (error) {
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await brandingAPI.update(formData);
      toast.success('Branding settings saved!');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5 MB)');
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await uploadAPI.logo(file);
      setFormData((prev) => ({ ...prev, logo_url: res.data.url }));
      toast.success('Logo uploaded. Click "Save Branding" to apply.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearLogo = () => {
    setFormData((prev) => ({ ...prev, logo_url: '' }));
  };

  if (loading) {
    return (
      <Layout activeNav="branding">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeNav="branding">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Branding Settings</h1>
          <p className="text-muted-foreground">
            Customize your organization's branding across the app and certificates
          </p>
        </div>

        {/* Email / Resend status banner */}
        {emailStatus && (
          <Card
            className={`mb-6 border-2 ${
              emailStatus.is_using_resend_default_domain
                ? 'border-amber-300 bg-amber-50/60'
                : emailStatus.api_key_configured
                  ? 'border-emerald-300 bg-emerald-50/60'
                  : 'border-rose-300 bg-rose-50/60'
            }`}
            data-testid="email-status-banner"
          >
            <CardContent className="p-4 flex items-start gap-3">
              {emailStatus.is_using_resend_default_domain ? (
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              ) : emailStatus.api_key_configured ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Mail className="w-4 h-4 text-foreground" />
                  <h3 className="font-medium text-foreground">Email delivery (Resend)</h3>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Sender address: <code className="px-1 py-0.5 rounded bg-muted text-xs">{emailStatus.sender_email || '(not configured)'}</code>
                </p>
                <p className="text-sm text-foreground/80 mt-2">{emailStatus.guidance}</p>
                {emailStatus.is_using_resend_default_domain && (
                  <a
                    href="https://resend.com/domains"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-2 text-sm text-primary underline"
                    data-testid="resend-verify-link"
                  >
                    Verify a domain on resend.com →
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          {/* Organization Details */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5" />
                Organization Details
              </CardTitle>
              <CardDescription>
                These details appear on certificates and throughout the app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization Name</Label>
                <Input
                  id="organization_name"
                  placeholder="Infinity Imaging & Interventions"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  className="h-11"
                  data-testid="org-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="Level 2, 123 Medical Street, Sydney NSW 2000"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="h-11"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="events@infinityimaging.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    placeholder="+61 2 1234 5678"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="h-11"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signatory */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Certificate Signatory
              </CardTitle>
              <CardDescription>
                Default signatory for certificates (can be overridden per event)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="signatory_name">Signatory Name</Label>
                  <Input
                    id="signatory_name"
                    placeholder="IIDA Educational Convenor"
                    value={formData.signatory_name}
                    onChange={(e) => setFormData({ ...formData, signatory_name: e.target.value })}
                    className="h-11"
                    data-testid="signatory-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signatory_title">Signatory Title</Label>
                  <Input
                    id="signatory_title"
                    placeholder="Medical Director"
                    value={formData.signatory_title}
                    onChange={(e) => setFormData({ ...formData, signatory_title: e.target.value })}
                    className="h-11"
                  />
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="mb-3">
                  <p className="text-sm font-medium">President Signatory</p>
                  <p className="text-xs text-muted-foreground">Used on post-event sponsor thank-you letters. Leave name blank to fall back to the certificate signatory above.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="president_name">President Name</Label>
                    <Input
                      id="president_name"
                      placeholder="Dr First Last"
                      value={formData.president_name}
                      onChange={(e) => setFormData({ ...formData, president_name: e.target.value })}
                      className="h-11"
                      data-testid="president-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="president_title">President Title</Label>
                    <Input
                      id="president_title"
                      placeholder="President"
                      value={formData.president_title}
                      onChange={(e) => setFormData({ ...formData, president_title: e.target.value })}
                      className="h-11"
                      data-testid="president-title-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax & Banking */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5" />
                Tax &amp; Banking
              </CardTitle>
              <CardDescription>
                Used on tax invoices and auto-filled as the default payment details for new events.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="abn">ABN</Label>
                <Input
                  id="abn"
                  placeholder="43 737 103 895"
                  value={formData.abn}
                  onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                  className="h-11"
                  data-testid="branding-abn-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank_account_name">Account name</Label>
                <Input
                  id="bank_account_name"
                  placeholder="Illawarra Indian Doctors Association"
                  value={formData.bank_account_name}
                  onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
                  className="h-11"
                  data-testid="branding-bank-name-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_bsb">BSB</Label>
                  <Input
                    id="bank_bsb"
                    placeholder="062 644"
                    value={formData.bank_bsb}
                    onChange={(e) => setFormData({ ...formData, bank_bsb: e.target.value })}
                    className="h-11"
                    data-testid="branding-bsb-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account number</Label>
                  <Input
                    id="bank_account_number"
                    placeholder="10464915"
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                    className="h-11"
                    data-testid="branding-acct-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="w-5 h-5" />
                Logo
              </CardTitle>
              <CardDescription>
                Upload your organization logo or paste a URL. Appears on certificates & registration pages.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload */}
              <div className="space-y-2">
                <Label>Upload Logo</Label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    onChange={handleLogoFileChange}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="gap-2"
                    data-testid="logo-upload-btn"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploadingLogo ? 'Uploading...' : 'Choose Image'}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, WEBP, GIF up to 5 MB
                  </p>
                </div>
              </div>

              {/* OR URL */}
              <div className="space-y-2">
                <Label htmlFor="logo_url">Or paste Logo URL</Label>
                <Input
                  id="logo_url"
                  placeholder="https://example.com/logo.png or /api/uploads/..."
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="h-11"
                  data-testid="logo-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Recommended size: 200x50 pixels. Logo is rendered at the top of every certificate.
                </p>
              </div>

              {formData.logo_url && (
                <div className="p-4 bg-muted rounded-lg flex items-start justify-between gap-3" data-testid="logo-preview-container">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Preview:</p>
                    <img 
                      src={resolveAssetUrl(formData.logo_url)} 
                      alt="Logo preview" 
                      className="max-h-16"
                      onError={(e) => e.target.style.display = 'none'}
                      data-testid="logo-preview-img"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={clearLogo}
                    title="Remove logo"
                    data-testid="logo-clear-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Colors */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Brand Colors
              </CardTitle>
              <CardDescription>
                These colors are used for certificates and branding elements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary_color"
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-16 h-11 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="h-11 flex-1"
                      placeholder="#0F172A"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accent_color">Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="accent_color"
                      type="color"
                      value={formData.accent_color}
                      onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                      className="w-16 h-11 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.accent_color}
                      onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                      className="h-11 flex-1"
                      placeholder="#0EA5E9"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={saving}
              className="gap-2"
              data-testid="save-branding-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Branding
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
